import { atom, Getter } from "jotai";
import { ShareGraphQlAdapter } from "@/infrastructure/adapters/graphQl/ShareGraphQl.adapter";
import { AnalyticsRepository } from "@/infrastructure/repositories/analytics/Analytics.repository";
import { NumberFormatterService } from "@/core/services/NumberFormatter.service";
import { NumberFormatterAdapter } from "@/infrastructure/adapters/number";
import { GetAnalyticsScoreCardsUseCase } from "@/core/usecases/analytics/GetAnalyticsScoreCards.useCase";
import {
  GetDeductionsByPurchaserUseCase,
  GetDeductionsByTypeAndReasonUseCase,
  GetDeductionVolumeVsGrossSalesUseCase,
  GetDeductionsByAgingBracketsUseCase,
  GetDisputePipelineUseCase,
  GetDeductionsByRetailerUseCase,
} from "@/core/usecases/analytics";
import {
  analyticsScoreCardsAtom,
  analyticsDeductionsByPurchaserAtom,
  analyticsDeductionsByTypeAndReasonAtom,
  analyticsDeductionVolumeVsGrossSalesAtom,
  analyticsDeductionsByAgingBracketsAtom,
  analyticsDisputePipelineAtom,
  analyticsDeductionsByRetailerAtom,
} from "@/store/analytics/analytics.atoms";
import { shareLinkDataAtom } from "@/store/shareLink/shareLink.atoms";
import { IAnalyticsRepositoryParams } from "@/core/repositories/analytics";

const numberFormatterService = new NumberFormatterService(new NumberFormatterAdapter());

const getSharedAnalyticsRepo = () => new AnalyticsRepository(ShareGraphQlAdapter.getInstance().getClient());

const getSharedQueryParams = (get: Getter): IAnalyticsRepositoryParams => {
  const payload = get(shareLinkDataAtom)?.resourcePayload;
  return {
    startDate: payload?.start_date ?? "",
    endDate: payload?.end_date ?? "",
    purchaserIds: payload?.purchaser_ids ?? [],
  };
};

export const fetchSharedAnalyticsScoreCardsAtom = atom(null, async (get, set) => {
  set(analyticsScoreCardsAtom, {
    ...get(analyticsScoreCardsAtom),
    unresolvedDeductions: { ...get(analyticsScoreCardsAtom).unresolvedDeductions, isLoading: true, error: null },
    raisedForDispute: { ...get(analyticsScoreCardsAtom).raisedForDispute, isLoading: true, error: null },
    deductionsGrossSales: { ...get(analyticsScoreCardsAtom).deductionsGrossSales, isLoading: true, error: null },
  });

  try {
    const params = getSharedQueryParams(get);
    const result = await new GetAnalyticsScoreCardsUseCase(getSharedAnalyticsRepo()).execute(params);

    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      unresolvedDeductions: {
        ...get(analyticsScoreCardsAtom).unresolvedDeductions,
        primaryValue: `$${numberFormatterService.format(Number(result.UNRESOLVED_DEDUCTIONS.primaryValue), "integer")}`,
        secondaryValue: `${result.UNRESOLVED_DEDUCTIONS.secondaryValue} ${Number(result.UNRESOLVED_DEDUCTIONS.secondaryValue) > 1 ? "deductions" : "deduction"}`,
        chipLabel: `${result.UNRESOLVED_DEDUCTIONS.chipLabel.includes("No") ? result.UNRESOLVED_DEDUCTIONS.chipLabel : `${result.UNRESOLVED_DEDUCTIONS.chipLabel} vs prior`}`,
        footerText: result.UNRESOLVED_DEDUCTIONS.footerText,
        trend: result.UNRESOLVED_DEDUCTIONS.trend,
        tooltipMarkdown: result.UNRESOLVED_DEDUCTIONS.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
      raisedForDispute: {
        ...get(analyticsScoreCardsAtom).raisedForDispute,
        primaryValue: `$${numberFormatterService.format(Number(result.RAISED_FOR_DISPUTE.primaryValue), "integer")}`,
        secondaryValue: `${result.RAISED_FOR_DISPUTE.secondaryValue} ${Number(result.RAISED_FOR_DISPUTE.secondaryValue) > 1 ? "disputes" : "dispute"}`,
        chipLabel: result.RAISED_FOR_DISPUTE.chipLabel,
        footerText: result.RAISED_FOR_DISPUTE.footerText,
        trend: result.RAISED_FOR_DISPUTE.trend,
        tooltipMarkdown: result.RAISED_FOR_DISPUTE.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
      deductionsGrossSales: {
        ...get(analyticsScoreCardsAtom).deductionsGrossSales,
        primaryValue: `$${numberFormatterService.format(Number(result.GROSS_SALE.primaryValue), "integer")}`,
        secondaryValue: `$${numberFormatterService.format(Number(result.GROSS_SALE.secondaryValue), "integer")} deducted`,
        chipLabel: result.GROSS_SALE.chipLabel,
        footerText: result.GROSS_SALE.footerText,
        trend: result.GROSS_SALE.trend,
        tooltipMarkdown: result.GROSS_SALE.tooltipMarkdown,
        isLoading: false,
        error: null,
      },
    });
  } catch (error: unknown) {
    set(analyticsScoreCardsAtom, {
      ...get(analyticsScoreCardsAtom),
      unresolvedDeductions: { ...get(analyticsScoreCardsAtom).unresolvedDeductions, isLoading: false, error: error as Error },
      raisedForDispute: { ...get(analyticsScoreCardsAtom).raisedForDispute, isLoading: false, error: error as Error },
      deductionsGrossSales: { ...get(analyticsScoreCardsAtom).deductionsGrossSales, isLoading: false, error: error as Error },
    });
  }
});

export const fetchSharedDeductionsByPurchaserAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByPurchaserAtom, { ...get(analyticsDeductionsByPurchaserAtom), isLoading: true, error: null });
  try {
    const items = await new GetDeductionsByPurchaserUseCase(getSharedAnalyticsRepo()).execute(getSharedQueryParams(get));
    set(analyticsDeductionsByPurchaserAtom, { items, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByPurchaserAtom, { ...get(analyticsDeductionsByPurchaserAtom), isLoading: false, error: error as Error });
  }
});

export const fetchSharedDeductionsByTypeAndReasonAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByTypeAndReasonAtom, { ...get(analyticsDeductionsByTypeAndReasonAtom), isLoading: true, error: null });
  try {
    const groups = await new GetDeductionsByTypeAndReasonUseCase(getSharedAnalyticsRepo()).execute(getSharedQueryParams(get));
    set(analyticsDeductionsByTypeAndReasonAtom, { groups, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByTypeAndReasonAtom, { ...get(analyticsDeductionsByTypeAndReasonAtom), isLoading: false, error: error as Error });
  }
});

export const fetchSharedDeductionVolumeVsGrossSalesAtom = atom(null, async (get, set) => {
  set(analyticsDeductionVolumeVsGrossSalesAtom, { ...get(analyticsDeductionVolumeVsGrossSalesAtom), isLoading: true, error: null });
  try {
    const items = await new GetDeductionVolumeVsGrossSalesUseCase(getSharedAnalyticsRepo()).execute(getSharedQueryParams(get));
    set(analyticsDeductionVolumeVsGrossSalesAtom, { items, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionVolumeVsGrossSalesAtom, { ...get(analyticsDeductionVolumeVsGrossSalesAtom), isLoading: false, error: error as Error });
  }
});

export const fetchSharedDeductionsByAgingBracketsAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByAgingBracketsAtom, { ...get(analyticsDeductionsByAgingBracketsAtom), isLoading: true, error: null });
  try {
    const data = await new GetDeductionsByAgingBracketsUseCase(getSharedAnalyticsRepo()).execute(getSharedQueryParams(get));
    set(analyticsDeductionsByAgingBracketsAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByAgingBracketsAtom, { ...get(analyticsDeductionsByAgingBracketsAtom), isLoading: false, error: error as Error });
  }
});

export const fetchSharedDisputePipelineAtom = atom(null, async (get, set) => {
  set(analyticsDisputePipelineAtom, { ...get(analyticsDisputePipelineAtom), isLoading: true, error: null });
  try {
    const params = getSharedQueryParams(get);
    const data = await new GetDisputePipelineUseCase(getSharedAnalyticsRepo()).execute({
      ...params,
      purchaserIds: params.purchaserIds.length ? params.purchaserIds : [],
    });
    set(analyticsDisputePipelineAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDisputePipelineAtom, { ...get(analyticsDisputePipelineAtom), isLoading: false, error: error as Error });
  }
});

export const fetchSharedDeductionsByRetailerAtom = atom(null, async (get, set) => {
  set(analyticsDeductionsByRetailerAtom, { ...get(analyticsDeductionsByRetailerAtom), isLoading: true, error: null });
  try {
    const data = await new GetDeductionsByRetailerUseCase(getSharedAnalyticsRepo()).execute(getSharedQueryParams(get));
    set(analyticsDeductionsByRetailerAtom, { data, isLoading: false, error: null });
  } catch (error: unknown) {
    set(analyticsDeductionsByRetailerAtom, { ...get(analyticsDeductionsByRetailerAtom), isLoading: false, error: error as Error });
  }
});

export const fetchAllSharedAnalyticsAtom = atom(null, async (_get, set) => {
  await Promise.all([
    set(fetchSharedAnalyticsScoreCardsAtom),
    set(fetchSharedDeductionsByPurchaserAtom),
    set(fetchSharedDeductionsByTypeAndReasonAtom),
    set(fetchSharedDeductionVolumeVsGrossSalesAtom),
    set(fetchSharedDeductionsByAgingBracketsAtom),
    set(fetchSharedDisputePipelineAtom),
    set(fetchSharedDeductionsByRetailerAtom),
  ]);
});
